import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ActionItemTracker = ({ actionItems, onAddItem, onUpdateItem, onDeleteItem, canEdit }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({
    title: '',
    assignee: '',
    dueDate: '',
    priority: 'medium',
    status: 'pending'
  });

  const priorityOptions = [
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'blocked', label: 'Blocked' }
  ];

  const handleAddItem = () => {
    if (newItem?.title && newItem?.assignee) {
      onAddItem({
        ...newItem,
        id: Date.now(),
        createdDate: new Date()?.toISOString()
      });
      setNewItem({ title: '', assignee: '', dueDate: '', priority: 'medium', status: 'pending' });
      setShowAddForm(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'blocked':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'completed') return false;
    return new Date(dueDate) < new Date();
  };

  const pendingCount = actionItems?.filter(item => item?.status === 'pending')?.length;
  const completedCount = actionItems?.filter(item => item?.status === 'completed')?.length;

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Icon name="CheckSquare" size={20} className="text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Action Items</h3>
            <p className="text-xs text-muted-foreground">
              {completedCount} of {actionItems?.length} completed
            </p>
          </div>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            Add
          </Button>
        )}
      </div>
      {showAddForm && (
        <div className="mb-4 p-3 bg-muted/30 rounded-lg border border-border">
          <Input
            label="Action Item"
            type="text"
            placeholder="Enter action item description"
            value={newItem?.title}
            onChange={(e) => setNewItem({ ...newItem, title: e?.target?.value })}
            className="mb-3"
          />
          <Input
            label="Assignee"
            type="text"
            placeholder="Enter assignee name"
            value={newItem?.assignee}
            onChange={(e) => setNewItem({ ...newItem, assignee: e?.target?.value })}
            className="mb-3"
          />
          <Input
            label="Due Date"
            type="date"
            value={newItem?.dueDate}
            onChange={(e) => setNewItem({ ...newItem, dueDate: e?.target?.value })}
            className="mb-3"
          />
          <Select
            label="Priority"
            options={priorityOptions}
            value={newItem?.priority}
            onChange={(value) => setNewItem({ ...newItem, priority: value })}
            className="mb-3"
          />
          <div className="flex gap-2">
            <Button variant="default" size="sm" onClick={handleAddItem}>
              Add Action
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
        {actionItems?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="CheckSquare" size={48} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No action items yet</p>
          </div>
        ) : (
          actionItems?.map((item) => (
            <div
              key={item?.id}
              className={`p-3 rounded-lg border transition-colors ${
                item?.status === 'completed'
                  ? 'bg-muted/20 border-border/50 opacity-75' :'bg-card border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={item?.status === 'completed'}
                  onChange={(e) => {
                    if (canEdit) {
                      onUpdateItem(item?.id, {
                        status: e?.target?.checked ? 'completed' : 'pending'
                      });
                    }
                  }}
                  disabled={!canEdit}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-foreground mb-2 ${item?.status === 'completed' ? 'line-through' : ''}`}>
                    {item?.title}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(item?.priority)}`}>
                      {item?.priority}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(item?.status)}`}>
                      {item?.status?.replace('-', ' ')}
                    </span>
                    {isOverdue(item?.dueDate, item?.status) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                        <Icon name="AlertCircle" size={12} />
                        Overdue
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      <span>{item?.assignee}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} />
                      <span>{formatDate(item?.dueDate)}</span>
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <button
                    onClick={() => onDeleteItem(item?.id)}
                    className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                    aria-label="Delete action item"
                  >
                    <Icon name="Trash2" size={14} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActionItemTracker;